import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Snackbar } from "@material-ui/core";
import { SnackbarContent } from "../../styled-components/shared";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import { Modal } from "../common/Modal";
import { createPrimaryItem, createGhostItem } from "../common/modalItems";
import { AuthContext } from "../../authentication/Auth";
import "@zebra-fed/icons/Alert/index.css";
import { InputField } from "../common/InputField";
import { DropdownField } from "../common/DropdownField";

import app from "../../authentication/firebaseStart";
import { usePermissions } from "../../authentication/rbac";
import { DEVICE_STATUS } from "../../pages/Dashboard/constant/Dashboard.constant";

const firestore = app.firestore();

const useStyles = makeStyles(() => ({
  scrollableContent: {
    maxHeight: "calc(100vh - 200px)",
    overflowY: "auto",
  },
  mainContent: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  sectionHeader: {
    fontSize: 14,
    fontWeight: 400,
    margin: 0,
    color: "#636e72",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  row: {
    display: "flex",
    gap: 8,
  },
  siteName: {
    overflow: "auto",
    zIndex: 9999,
  },
}));

const defaultDialogProps = {
  width: "640px",
};
const statusList = {
    "In Use": [{ label: "In Use", value: 'In Use' }    
    ],
    "Missing": [{ label: "Missing", value: 'Missing' }
    ],
    "Available": [{ label: "Available", value: 'Available' }]
}

function KioskDeviceModal({
  modalRef,
  onClose,
  initialData = null,
  onDeviceUpdated = null,
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const context = useContext(AuthContext);
  const {
    canCreateKioskDevice,
    canUpdateKioskDevice,
    canDeleteKioskDevice,
    canSendAlarm,
    canMarkDeviceReCommissioned,
    canMarkDeviceDeCommissioned,
    canMarkDeviceLost,
    canMarkDeviceFound,
    canUpdateAlias,
    canUpdateNameKioskDevice
  } = usePermissions();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const validationSchema = Yup.object().shape({
    deviceName: Yup.string().required(t("deviceNameRequired")),
    deviceStatusText: Yup.string().required(
      t("validation.rules.Mo_Dash.RequiredDropdownMessage")
    ),
  });

  const getInitialValues = () => {
    return {
      deviceName: initialData?.__name__ || "",
      kioskName: "",
      friendlyName: "",
      deviceStatusText: "",
      disabled: true,
      ...initialData,
    };
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    validateOnChange: true,
    isInitialValid: true,
    onSubmit: (values) => {
      const trimmedValues = {
        ...values,
        kioskName: values.kioskName.trim(),
        friendlyName: values.friendlyName.trim(),
      };
      handleSubmit(trimmedValues);
    },
  });

  async function handleSubmit(values) {
    setIsLoading(true);
    try {
      const companyID = context?.currentUser?.claims?.companyID;
      //DocumentId will be modelNumber_serialNuber
      let documentId =
        initialData.__name__ || values.modelNumber + "_" + values.serialNumber;

      if (initialData.__name__ === values.deviceName) {
        const device = {
          friendlyName: values.friendlyName || "",
        };
        switch (values.deviceStatusText) {
          case "Decommission":
            device.kioskName = "";
            device.deviceState = DEVICE_STATUS.decommission;
            break;
          case "Recommission":
            device.kioskName = "";
            device.deviceState = DEVICE_STATUS.recommission;
            break;
          case "Lost":
            device.kioskName = "";
            device.deviceState = DEVICE_STATUS.lost;
            break;
          default:
            break;
        }
        await firestore
          .collection("tenants")
          .doc(companyID)
          .collection("Devices")
          .doc(documentId)
          .update({
            ...device,
          });
      } else {
        const device = {
          friendlyName: values.friendlyName || "",
        };
        switch (values.deviceStatusText) {
          case "Decommission":
            device.kioskName = "";
            device.deviceState = DEVICE_STATUS.decommission;
            break;
          case "Recommission":
            device.kioskName = "";
            device.deviceState = DEVICE_STATUS.recommission;
            break;
          case "Lost":
            device.kioskName = "";
            device.deviceState = DEVICE_STATUS.lost;
            break;
          default:
            break;
        }
        const oldDocRef = firestore
          .collection("tenants")
          .doc(companyID)
          .collection("Devices")
          .doc(documentId);
        const newDocRef = firestore
          .collection("tenants")
          .doc(companyID)
          .collection("Devices")
          .doc(values.deviceName);
        const oldDoc = await oldDocRef.get();
        if (oldDoc.exists) {
          const data = oldDoc.data();
          //create new doc
          await newDocRef.set({ ...data, ...device });

          //delete old doc
          await oldDocRef.delete();
        }
      }

      setIsLoading(false);
      onClose();
      modalRef.current.toggleDialog();
      formik.resetForm();
      onDeviceUpdated && onDeviceUpdated();
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setSnackbarOpen(true);
    }
  }

  const handleCancel = () => {
    modalRef.current.toggleDialog();
    formik.resetForm();
  };

  const isFormValid = () => {
    return formik.isValid && Object.keys(formik.errors).length === 0;
  };

  const getModalActions = () => {
    const actions = [
      createGhostItem({
        onClick: handleCancel,
        disabled: isLoading,
        children: t("cancel"),
      }),
      createPrimaryItem({
        onClick: formik.handleSubmit,
        disabled: isLoading || !isFormValid() || !formik.dirty,
        children: t("update"),
      }),
    ];
    return actions;
  };

  // Return null if user doesn't have required permissions
  if (!canUpdateKioskDevice) {
    return null;
  }

  return (
    <>
      <Modal
        modalRef={modalRef}
        title={initialData?.displayDeviceFriendlyName}
        actions={getModalActions()}
        dialogProps={{
          ...defaultDialogProps,
          style: { cursor: isLoading ? "wait" : "default" },
        }}
      >
        <div className={classes.scrollableContent}>
          <div className={classes.mainContent}>
            <div className={classes.section}>
              <Typography className={classes.sectionHeader}>
                {t("basicInformation")}
              </Typography>
              <div className={classes.row}>
                <InputField
                  label={t("deviceDetail.deviceName")}
                  fullWidth={true}
                  required={true}
                  disabled={!canUpdateNameKioskDevice}
                  {...formik.getFieldProps("deviceName")}
                  error={formik.touched.deviceName && formik.errors.deviceName}
                  helperText={
                    formik.touched.deviceName && formik.errors.deviceName
                  }
                  inputProps={{ maxLength: 50 }}
                />
                <InputField
                  label={t("kioskName")}
                  fullWidth={true}
                  required={true}
                  {...formik.getFieldProps("kioskName")}
                  error={formik.touched.kioskName && formik.errors.kioskName}
                  helperText={
                    formik.touched.kioskName && formik.errors.kioskName
                  }
                  inputProps={{ maxLength: 50 }}
                  disabled={true}
                />
              </div>
              <div className={classes.row}>
                <InputField
                  label={t("alias")}
                  fullWidth={true}
                  required={false}
                  disabled={!canUpdateAlias}
                  {...formik.getFieldProps("friendlyName")}
                  error={formik.touched.friendlyName && formik.errors.friendlyName}
                  helperText={formik.touched.friendlyName && formik.errors.friendlyName}
                  inputProps={{ maxLength: 50 }}
                />
                <DropdownField
                  label={t("status")}
                  fullWidth={true}
                  required={true}
                  options={statusList[initialData?.deviceStatusText] || []}
                  value={formik.values?.deviceStatusText || ""}
                  onChange={(event) =>
                    formik.setFieldValue("deviceStatusText", event.target.value)
                  }
                  error={
                    formik.touched.deviceStatusText &&
                    formik.errors.deviceStatusText
                  }
                  helperText={
                    formik.touched.deviceStatusText &&
                    formik.errors.deviceStatusText
                  }
                  openDropDownInBody={true}
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
      >
        <SnackbarContent error={true}>
          {t("Error updating device")}
        </SnackbarContent>
      </Snackbar>
    </>
  );
}

export default KioskDeviceModal;
